// src/pages/Home.jsx
import React, { useEffect, useRef, useState } from "react";
import Seo from "../components/Seo";
import StructuredData from "../components/StructuredData";
import { DEFAULT_DESCRIPTION } from "../seoConfig";
import chooseUsImage01 from "../assets/images/choose-us-image-01.png";
import chooseUsImage02 from "../assets/images/choose-us-image-02.png";
import chooseUsImage03 from "../assets/images/choose-us-image-03.png";
import homeBackground from "../assets/images/homebackground.jpeg";

import TopBar from "../components/home/TopBar";
import Footer from "../components/home/Footer";
import GlowEdge from "../components/ui/GlowEdge";
import useRevealOncePerSession from "../hooks/useRevealOncePerSession";
import useFitText from "../hooks/useFitText";
const DEFAULT_CARD_LINK = {
  url: "https://maps.app.goo.gl/qKWDmbQRu1Kkr7mE9",
  openInNewTab: true,
};
import { db } from "../firebase";
import { doc, getDoc, runTransaction, setDoc, serverTimestamp, onSnapshot, collection } from "firebase/firestore";

const useLegacyScripts = () => {
  useEffect(() => {
    const inject = (file) =>
      new Promise((resolve, reject) => {
        const url = new URL(`../assets/js/${file}`, import.meta.url).href;
        const s = Object.assign(document.createElement("script"), {
          src: url,
          async: false,
          onload: resolve,
          onerror: reject,
        });
        document.body.appendChild(s);
      });

    (async () => {
      await inject("isotope.min.js");
      await inject("owl-carousel.js");
      await inject("lightbox.js");
      await inject("tabs.js");
      await inject("video.js");
      await inject("slick-slider.js");
      await inject("custom.js");
    })();
  }, []);
};

function Home() {
  useLegacyScripts();
  const pageRef = useRef(null);
  useRevealOncePerSession("home", { scope: pageRef });
  // Prefer last-known values to avoid flicker before Firestore snapshot arrives
  const annCache = (() => {
    try {
      const raw = localStorage.getItem('lia_home_announcement');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const titlesCache = (() => {
    try {
      const raw = localStorage.getItem('lia_home_titles');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const cardCache = (() => {
    try {
      const raw = localStorage.getItem('lia_home_card');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const enrollCache = (() => {
    try {
      const raw = localStorage.getItem('lia_enroll_button');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [haEnabled, setHaEnabled] = useState(() => Boolean(annCache?.enabled ?? false));
  const [haContent, setHaContent] = useState(() => String(annCache?.content ?? ""));
  const [haBg, setHaBg] = useState(() => String(annCache?.bgColor ?? "#F5F5DC"));
  const [haBorder, setHaBorder] = useState(() => String(annCache?.borderColor ?? "#e6e6b8"));
  const [haText, setHaText] = useState(() => String(annCache?.textColor ?? "#000000"));
  const [haReady, setHaReady] = useState(!!annCache);
  const [pdfMap, setPdfMap] = useState(new Map());
  const [linkMap, setLinkMap] = useState(new Map());
  const [imageMap, setImageMap] = useState(new Map()); // id -> {url, width}
  // Home Titles settings
  const [homeTitle1, setHomeTitle1] = useState(() => (titlesCache?.title1 ?? { enabled: false, text: "", fontSize: "clamp(18px, 5vw, 72px)", fontWeight: 700, color: "#ffffff" }));
  const [homeTitle2, setHomeTitle2] = useState(() => (titlesCache?.title2 ?? { enabled: false, text: "", fontSize: "clamp(14px, 3.2vw, 22px)", fontWeight: 550, color: "#ffffff" }));
  const [homeTitle3, setHomeTitle3] = useState(() => (titlesCache?.title3 ?? { enabled: false, text: "", fontSize: "clamp(18px, 3.6vw, 28px)", fontWeight: 550, color: "#ffffff" }));
  // Home Card (Open House) settings
  const [homeCardDate, setHomeCardDate] = useState(() => (cardCache?.date ?? { enabled: false, line1: 'SEP. 6', line2: '2025', color: '#0B2A5B', fontWeight: 700, fontSizeMobile: 'clamp(32px, 6vw, 36px)', fontSizeDesktop: 'clamp(22px, 5.2vw, 32px)' }));
  const [homeCardText, setHomeCardText] = useState(() => (cardCache?.text ?? { enabled: false, line1: 'OPEN HOUSE &', line2: 'FREE EVALUATION', color: '#0B2A5B', fontWeight: 600, fontSizeMobile: 'clamp(16px, 4vw, 28px)', fontSizeDesktop: 'clamp(18px, 3.6vw, 28px)' }));
  const [homeCardDateLink, setHomeCardDateLink] = useState(() => sanitizeHomeCardLink(cardCache?.dateLink ?? cardCache?.link ?? DEFAULT_CARD_LINK));
  const [homeCardTextLink, setHomeCardTextLink] = useState(() => sanitizeHomeCardLink(cardCache?.textLink ?? cardCache?.link ?? DEFAULT_CARD_LINK));
  const [enroll, setEnroll] = useState(() => (enrollCache ?? { enabled: true, label: 'ENROLL NOW', url: '/programs', openInNewTab: false, buttonColor: '#ffbf1b', glowColor: '#ffbf1b', textColor: '#003071' }));
  // Count only once per browser session, also write to Firestore: meta/homeVisits.total + 1
  useEffect(() => {
    const KEY = "lia_home_visit_counted"; // Keep consistent with historical versions, share once across Home variants
    if (sessionStorage.getItem(KEY)) return;
    const counterRef = doc(db, "meta", "homeVisits");
    (async () => {
      try {
        const snap = await getDoc(counterRef);
        if (!snap.exists()) {
          // Rules allow creation without login and total must be 0
          await setDoc(counterRef, { total: 0 });
        }
        await runTransaction(db, async (tx) => {
          const cur = await tx.get(counterRef);
          const val = (cur.data()?.total || 0) + 1;
          tx.update(counterRef, { total: val, updatedAt: serverTimestamp() });
        });
        sessionStorage.setItem(KEY, "1");
      } catch {
        // Ignore failures (don't set KEY, will retry later)
      }
    })();
  }, []);
  const pTwoLinesRef = useRef(null);
  const oneLine1Ref = useRef(null);
  const oneLine2Ref = useRef(null);
  const oneLine3Ref = useRef(null);
  const oneLine4Ref = useRef(null);
  const oneLine5Ref = useRef(null);
  const oneLine6Ref = useRef(null);
  const locAddr1Ref = useRef(null);
  const locAddr2Ref = useRef(null);
  const locNewLinkRef = useRef(null);
  const locDesktopLinkRef = useRef(null);
  const phone1MobileRef = useRef(null);
  const phone2MobileRef = useRef(null);
  const phone1DesktopRef = useRef(null);
  const phone2DesktopRef = useRef(null);

  useFitText(pTwoLinesRef, { maxLines: 3, minFontSizePx: 14, stepPx: 0.5 });
  useFitText(oneLine1Ref, { maxLines: 1, minFontSizePx: 14, stepPx: 0.5 });
  // Early Bird needs smaller minimum font size on narrow screens to avoid truncation
  useFitText(oneLine2Ref, { maxLines: 1, minFontSizePx: 10, stepPx: 0.5 });
  useFitText(oneLine3Ref, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(oneLine4Ref, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(oneLine5Ref, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(oneLine6Ref, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(locAddr1Ref, { maxLines: 2, minFontSizePx: 12, stepPx: 0.5 });
  // LOCATION (new site) link: fit to 3 lines on narrow screens
  useFitText(locNewLinkRef, { maxLines: 3, minFontSizePx: 8, stepPx: 0.5 });
  // Desktop Locations link: fit to 3 lines, prefer wrapping on wide screens for larger text
  useFitText(locDesktopLinkRef, { maxLines: 3, minFontSizePx: 9, stepPx: 0.5, preferWrapOnWide: true });
  // Mobile contact buttons: ensure phone fits within pill width
  useFitText(phone1MobileRef, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(phone2MobileRef, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(phone1DesktopRef, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });
  useFitText(phone2DesktopRef, { maxLines: 1, minFontSizePx: 12, stepPx: 0.5 });

  // Home Announcement realtime settings
  useEffect(() => {
    const unsubSettings = onSnapshot(doc(db, "site_settings", "home_announcement"), (snap) => {
      const data = snap.data() || {};
      setHaEnabled(Boolean(data.enabled ?? false));
      setHaContent(String(data.content ?? "PSAT Class Starts on 9/6"));
      setHaBg(String(data.bgColor ?? "#F5F5DC"));
      setHaBorder(String(data.borderColor ?? "#e6e6b8"));
      setHaText(String(data.textColor ?? "#000000"));
      try {
        localStorage.setItem('lia_home_announcement', JSON.stringify({
          enabled: Boolean(data.enabled ?? false),
          content: String(data.content ?? "PSAT Class Starts on 9/6"),
          bgColor: String(data.bgColor ?? "#F5F5DC"),
          borderColor: String(data.borderColor ?? "#e6e6b8"),
          textColor: String(data.textColor ?? "#000000"),
        }));
      } catch { /* ignore */ }
      setHaReady(true);
    });
    const unsubPdfs = onSnapshot(collection(db, "home_announce_pdfs"), (snap) => {
      const map = new Map();
      snap.docs.forEach((d) => {
        const v = d.data();
        if (v?.label && v?.url) map.set(String(v.label), String(v.url));
      });
      setPdfMap(map);
    });
    const unsubLinks = onSnapshot(collection(db, "home_announce_links"), (snap) => {
      const map = new Map();
      snap.docs.forEach((d) => {
        const v = d.data();
        if (v?.label && v?.url) map.set(String(v.label), String(v.url));
      });
      setLinkMap(map);
    });
    const unsubImages = onSnapshot(collection(db, "home_announce_images"), (snap) => {
      const map = new Map();
      snap.docs.forEach((d) => {
        const v = d.data();
        if (v?.label && v?.url) map.set(String(v.label), { url: String(v.url), width: Number(v.width || 0) });
      });
      setImageMap(map);
    });
    return () => { unsubSettings(); unsubPdfs(); unsubLinks(); unsubImages(); };
  }, []);

  // Home Titles realtime settings
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "site_settings", "home_titles"), (snap) => {
      const data = snap.data() || {};
      const t1 = data.title1 || {};
      const t2 = data.title2 || {};
      const t3 = data.title3 || {};
      const nt1 = {
        enabled: Boolean(t1.enabled ?? true),
        text: String(t1.text ?? "WE ARE IN SYOSSET!"),
        fontSize: String(t1.fontSize ?? "clamp(18px, 5vw, 72px)"),
        fontWeight: Number(t1.fontWeight ?? 700),
        color: String(t1.color ?? "#ffffff"),
      };
      const nt2 = {
        enabled: Boolean(t2.enabled ?? true),
        text: String(t2.text ?? "LIA was founded in Syosset in 1990. Now, we're excited to return to our roots! Brand-new Syosset campus this fall!"),
        fontSize: String(t2.fontSize ?? "clamp(14px, 3.2vw, 22px)"),
        fontWeight: Number(t2.fontWeight ?? 550),
        color: String(t2.color ?? "#ffffff"),
      };
      const nt3 = {
        enabled: Boolean(t3.enabled ?? true),
        text: String(t3.text ?? "Early Bird Special by Sep. 7"),
        fontSize: String(t3.fontSize ?? "clamp(18px, 3.6vw, 28px)"),
        fontWeight: Number(t3.fontWeight ?? 550),
        color: String(t3.color ?? "#ffffff"),
      };
      setHomeTitle1(nt1);
      setHomeTitle2(nt2);
      setHomeTitle3(nt3);
      try { localStorage.setItem('lia_home_titles', JSON.stringify({ title1: nt1, title2: nt2, title3: nt3 })); } catch { /* ignore */ }
    });
    return unsub;
  }, []);

  // Home Card realtime settings
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'site_settings', 'home_card'), (snap) => {
      const data = snap.data() || {};
      const d = data.date || {};
      const t = data.text || {};
      const nd = {
        enabled: Boolean(d.enabled ?? true),
        line1: String(d.line1 ?? 'SEP. 6'),
        line2: String(d.line2 ?? '2025'),
        color: String(d.color ?? '#0B2A5B'),
        fontWeight: Number(d.fontWeight ?? 700),
        fontSizeMobile: String(d.fontSizeMobile ?? 'clamp(32px, 6vw, 36px)'),
        fontSizeDesktop: String(d.fontSizeDesktop ?? 'clamp(22px, 5.2vw, 32px)'),
      };
      const nt = {
        enabled: Boolean(t.enabled ?? true),
        line1: String(t.line1 ?? 'OPEN HOUSE &'),
        line2: String(t.line2 ?? 'FREE EVALUATION'),
        color: String(t.color ?? '#0B2A5B'),
        fontWeight: Number(t.fontWeight ?? 600),
        fontSizeMobile: String(t.fontSizeMobile ?? 'clamp(16px, 4vw, 28px)'),
        fontSizeDesktop: String(t.fontSizeDesktop ?? 'clamp(18px, 3.6vw, 28px)'),
      };
      const fallbackLink = sanitizeHomeCardLink(data.link);
      const nDateLink = sanitizeHomeCardLink(data.dateLink ?? fallbackLink);
      const nTextLink = sanitizeHomeCardLink(data.textLink ?? fallbackLink);
      setHomeCardDate(nd);
      setHomeCardText(nt);
      setHomeCardDateLink(nDateLink);
      setHomeCardTextLink(nTextLink);
      try {
        localStorage.setItem('lia_home_card', JSON.stringify({
          date: nd,
          text: nt,
          link: nDateLink, // legacy cache fallback
          dateLink: nDateLink,
          textLink: nTextLink,
        }));
      } catch { /* ignore */ }
    });
    return unsub;
  }, []);

  // Enroll Button realtime settings
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'site_settings', 'enroll_button'), (snap) => {
      const v = snap.data() || {};
      const nv = {
        enabled: Boolean(v.enabled ?? true),
        label: String(v.label ?? 'ENROLL NOW'),
        url: String(v.url ?? '/programs'),
        openInNewTab: Boolean(v.openInNewTab ?? false),
        buttonColor: String(v.buttonColor ?? '#ffbf1b'),
        glowColor: String(v.glowColor ?? '#ffbf1b'),
        textColor: String(v.textColor ?? '#003071'),
      };
      setEnroll(nv);
      try { localStorage.setItem('lia_enroll_button', JSON.stringify(nv)); } catch { /* ignore */ }
    });
    return unsub;
  }, []);

  const renderRichContent = (text) => {
    const renderInlineBold = (s, keyPrefix = 'inline') => {
      const out = [];
      const boldRe = /\*\*([^*]+)\*\*/g;
      let i = 0; let m;
      while ((m = boldRe.exec(s)) !== null) {
        if (m.index > i) out.push(s.slice(i, m.index));
        out.push(<strong key={`${keyPrefix}-${out.length}`}>{m[1]}</strong>);
        i = boldRe.lastIndex;
      }
      if (i < s.length) out.push(s.slice(i));
      return out;
    };
    const parts = [];
    const regex = /\[\[(pdf|link|img)\|([^|\]]+)\|([^|\]]+)\]\]|\*\*([^*]+)\*\*/g;
    let lastIndex = 0;
    let m;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > lastIndex) {
        const plain = text.slice(lastIndex, m.index);
        parts.push(...plain.split("\n").flatMap((seg, idx, arr) => idx < arr.length - 1 ? [seg, <br key={`br-${lastIndex}-${idx}`} />] : [seg]));
      }
      if (m[1] === "pdf" || m[1] === "link") {
        const kind = m[1];
        const disp = m[2];
        const id = m[3];
        const href = kind === "pdf" ? (pdfMap.get(id) || "#") : (linkMap.get(id) || "#");
        parts.push(
          <a
            key={`k-${parts.length}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="hover-underline-inset"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {renderInlineBold(disp, `disp-${parts.length}`)}
          </a>
        );
      } else if (m[1] === "img") {
        const id = m[2];
        const widthToken = m[3];
        const info = imageMap.get(id);
        const src = info?.url || '';
        let styleWidth;
        if (/%$/.test(widthToken)) styleWidth = widthToken;
        else if (/^\d+$/.test(widthToken)) styleWidth = `${widthToken}px`;
        else if (info && Number(info.width || 0) > 0) styleWidth = `${Number(info.width)}px`;
        parts.push(
          src ? <img key={`img-${parts.length}`} src={src} alt="" style={{ width: styleWidth || 'auto', maxWidth: '100%', height: 'auto', verticalAlign: 'middle' }} /> : null
        );
      } else if (m[4]) {
        parts.push(<strong key={`b-${parts.length}`}>{m[4]}</strong>);
      }
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
      const tail = text.slice(lastIndex);
      parts.push(...tail.split("\n").flatMap((seg, idx, arr) => idx < arr.length - 1 ? [seg, <br key={`br-tail-${lastIndex}-${idx}`} />] : [seg]));
    }
    return parts;
  };

  // Render inline bold (Title 2 supports **bold** and line breaks)
  const renderInlineBold = (text) => {
    const pieces = [];
    const boldRe = /\*\*([^*]+)\*\*/g;
    let cursor = 0; let m;
    while ((m = boldRe.exec(text)) !== null) {
      if (m.index > cursor) pieces.push(text.slice(cursor, m.index));
      pieces.push(<strong key={`t2b-${pieces.length}`}>{m[1]}</strong>);
      cursor = boldRe.lastIndex;
    }
    if (cursor < text.length) pieces.push(text.slice(cursor));
    const out = [];
    let keySeq = 0;
    for (const seg of pieces) {
      if (typeof seg === 'string') {
        const arr = seg.split("\n");
        arr.forEach((s, idx) => {
          out.push(s);
          if (idx < arr.length - 1) out.push(<br key={`t2br-${keySeq++}`} />);
        });
      } else {
        out.push(seg);
      }
    }
    return out;
  };

  return (
    <>
      <Seo
        title="LIA Learning"
        description="Founded in 1990 in Syosset, NY, Long Island Academy helps K‑12 students improve academics and attitudes with dedicated teachers and a motivating curriculum."
        path="/"
        ogImage="/assets/images/logo/LIA_logo.png"
      />
      <StructuredData />
      <TopBar />

      <div ref={pageRef} style={{ background: '#003071' }}>
        {/* Hero: Background image + top deepening gradient + bottom mask fade */}
        <div
          className="reveal"
          style={{
            position: "relative",
            width: "100%",
            height: "40vh",
            minHeight: "40vh",
            marginTop: "80px",
            backgroundImage: `linear-gradient(to bottom, rgba(8,29,73,.65) 0%, rgba(8,29,73,.25) 45%, rgba(8,29,73,.05) 70%), url(${homeBackground})`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            WebkitMaskImage: 'linear-gradient(#000 72%, rgba(0,0,0,0) 100%)',
            maskImage: 'linear-gradient(#000 72%, rgba(0,0,0,0) 100%)',
          }}
        />

        <section className="section main-banner" id="top" data-section="section1" style={{ paddingTop: 30, minHeight: 'auto', background: '#003071' }}>
          <div className="header-text reveal">
            <div className="caption" style={{ padding: '80px 20px 50px' }}>
              <div
                className="announcement-container"
                style={{
                  width: '100%',
                  maxWidth: '100vw',
                  margin: '0 auto',
                  background: 'transparent',
                  fontFamily: 'Montserrat, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                }}
              >
                {/* 在公告准备好之前，预先占位避免后续渲染导致内容下移 */}
                {haEnabled ? (
                  haReady ? (
                    <div className="card p-3 my-3" style={{ background: haBg, border: `3px solid ${haBorder}` }}>
                      <div style={{ color: haText, fontWeight: 700, fontSize: 24, textAlign: 'center', margin: 0 }}>
                        {renderRichContent(haContent)}
                      </div>
                    </div>
                  ) : (
                    <div className="card p-3 my-3" style={{ background: '#F5F5DC', border: '3px solid #e6e6b8', visibility: 'hidden' }}>
                      <div style={{ color: '#000000', fontWeight: 700, fontSize: 24, textAlign: 'center', margin: 0 }}>
                        &nbsp;
                      </div>
                    </div>
                  )
                ) : null}

                {/* 新增分组容器，包裹标题、说明、按钮、Early Bird 与三张卡片，不改变任何样式 */}
                {(!haEnabled || haReady) && (
                  <div className="hero-announcement-group" style={{ background: 'transparent' }}>
                    {/* 轻微渐变背景（深蓝 → 透明 → 深蓝）增强标题可读性 */}
                    <div
                      style={{
                        background: 'linear-gradient(to bottom, rgba(0,48,113,0.6) 0%, rgba(0,48,113,0) 45%, rgba(0,48,113,0) 55%, rgba(0,48,113,0.6) 100%)',
                        padding: '12px 0',
                        marginTop: '0px',
                        borderRadius: 0,
                      }}
                    >
                      <h2
                        style={{
                          color: homeTitle1.color || "#fff",
                          fontWeight: homeTitle1.fontWeight,
                          fontSize: homeTitle1.fontSize,
                          marginBottom: -20,
                          marginTop: -10,
                          lineHeight: 1.05,
                          textAlign: 'center',
                          wordBreak: 'keep-all',
                          overflow: 'hidden',
                        }}
                      >
                        {homeTitle1.enabled && (
                          <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{homeTitle1.text}</span>
                        )}
                      </h2>
                    </div>

                    <div
                      className="syosset-announcement"
                      style={{
                        color: "#fff",
                        marginTop: 12,
                        textAlign: 'left',
                        fontFamily: 'Montserrat, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      }}
                    >
                      <p
                        ref={pTwoLinesRef}
                        style={{
                          fontSize: homeTitle2.fontSize,
                          margin: '6px 0 20px',
                          lineHeight: 1.35,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          wordBreak: 'word-break',
                          fontWeight: homeTitle2.fontWeight,
                          color: homeTitle2.color || '#fff',
                        }}
                      >
                        {homeTitle2.enabled ? renderInlineBold(homeTitle2.text) : null}
                      </p>

                      {/* CTA 容器与 Open House+Contact 竖向容器并排（无间隔） */}
                      <div
                        className="reveal delay-1"
                        style={{
                          display: 'flex',
                          gap: 0,
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          margin: '40px 0 10px',
                        }}
                      >
                        {/* CTA 组：ENROLL + Early Bird */}
                        <div
                          className="cta-group"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 20,
                            minWidth: 260,
                            boxSizing: 'border-box',
                            flex: '0 0 65%',
                            width: '65%',
                            maxWidth: 'none',
                            textAlign: 'center',
                          }}
                        >
                          <p style={{ fontWeight: 400, fontSize: 24, margin: 0 }}>
                            <a
                              href={enroll.url || '/programs'}
                              className="cta-scale-shadow"
                              style={{
                                color: enroll.textColor || "#003071",
                                textDecoration: "none",
                                fontWeight: 550,
                                fontSize: 'clamp(18px, 3.6vw, 28px)',
                                whiteSpace: 'nowrap',
                                lineHeight: 1.1,
                                display: enroll.enabled ? 'inline-block' : 'none',
                                background: enroll.buttonColor || '#ffbf1b',
                                borderRadius: 12,
                                padding: '12px 28px',
                                '--cta-glow': enroll.glowColor || '#ffbf1b',
                              }}
                              ref={oneLine1Ref}
                              target={enroll.openInNewTab ? '_blank' : undefined}
                              rel={enroll.openInNewTab ? 'noopener noreferrer' : undefined}
                            >
                              {enroll.label || 'ENROLL NOW'}
                            </a>
                          </p>
                          {homeTitle3.enabled ? (
                            <p
                              ref={oneLine2Ref}
                              style={{
                                fontWeight: homeTitle3.fontWeight,
                                fontSize: homeTitle3.fontSize,
                                margin: 5,
                                whiteSpace: 'nowrap',
                                lineHeight: 1.1,
                                textAlign: 'center',
                                color: homeTitle3.color || '#fff',
                              }}
                            >
                              {homeTitle3.text}
                            </p>
                          ) : (
                            <div style={{ height: 14 }} />
                          )}

                          {/* Mobile: Open House + Contact on the same row under CTA */}
                          <div className="mobile-only">
                            <div className="info-row info-row--two">
                              {/* Open House card (mobile) */}
                              <div
                                className="info-card card-scale-hover"
                                onMouseEnter={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.add('underline-active')); }}
                                onMouseLeave={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.remove('underline-active')); }}
                                onFocus={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.add('underline-active')); }}
                                onBlur={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.remove('underline-active')); }}
                                onTouchStart={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.add('underline-active')); }}
                                onTouchEnd={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.remove('underline-active')); }}
                              >
                                <div style={{ textAlign: 'left' }}>
                                  <h4
                                    className="oh-date-mobile"
                                    role={homeCardDate.enabled ? "button" : undefined}
                                    tabIndex={homeCardDate.enabled ? 0 : -1}
                                    onClick={homeCardDate.enabled ? () => openHomeCardLink(homeCardDateLink) : undefined}
                                    onKeyDown={homeCardDate.enabled ? (e) => handleHomeCardKeyDown(e, homeCardDateLink) : undefined}
                                    style={{
                                      margin: '6px 0 12px',
                                      fontWeight: homeCardDate.fontWeight,
                                      letterSpacing: '.5px',
                                      color: homeCardDate.color,
                                      fontSize: homeCardDate.fontSizeMobile,
                                      lineHeight: 1.1,
                                      cursor: homeCardDate.enabled ? 'pointer' : 'default',
                                    }}
                                  >
                                    {homeCardDate.enabled && (
                                      <>
                                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{homeCardDate.line1}</span>
                                        <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{homeCardDate.line2}</span>
                                      </>
                                    )}
                                  </h4>
                                  <div
                                    className="info-title info-title--open"
                                    role={homeCardText.enabled ? "button" : undefined}
                                    tabIndex={homeCardText.enabled ? 0 : -1}
                                    onClick={homeCardText.enabled ? () => openHomeCardLink(homeCardTextLink) : undefined}
                                    onKeyDown={homeCardText.enabled ? (e) => handleHomeCardKeyDown(e, homeCardTextLink) : undefined}
                                    style={{
                                      marginTop: 10,
                                      fontWeight: homeCardText.fontWeight,
                                      fontSize: homeCardText.fontSizeMobile,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 8,
                                      color: homeCardText.color,
                                      cursor: homeCardText.enabled ? 'pointer' : 'default',
                                    }}
                                  >
                                    {homeCardText.enabled && (
                                      <>
                                        <span ref={oneLine3Ref} className="line-1">{homeCardText.line1}</span>
                                        <span ref={oneLine6Ref} className="line-2">{homeCardText.line2}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Contact card (mobile) */}
                              <div className="info-card">
                                <div style={{ textAlign: 'center' }}>
                                  <div className="info-head">
                                    <i className="fa fa-phone info-icon" />
                                    <h4 className="info-title">
                                      <span className="label-full">OFFICE / TEXT</span>
                                      <span className="label-short">OFF / TXT</span>
                                    </h4>
                                  </div>
                                  <div className="contact-actions contact-actions--phones" style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', padding: '0 12px', boxSizing: 'border-box' }}>
                                    <a
                                      href="tel:+15167168888"
                                      className="hover-underline-inset"
                                      style={{
                                        color: '#0B2A5B',
                                        textDecoration: 'none',
                                        fontWeight: 700,
                                        fontSize: 'clamp(16px, 4.2vw, 20px)',
                                        lineHeight: 1.2,
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <span
                                        ref={phone1MobileRef}
                                        style={{ display: 'block', width: '100%', whiteSpace: 'nowrap' }}
                                      >
                                        Office: (516)716-8888
                                      </span>
                                    </a>
                                    <a
                                      href="tel:+15166906900"
                                      className="hover-underline-inset"
                                      style={{
                                        color: '#0B2A5B',
                                        textDecoration: 'none',
                                        fontWeight: 700,
                                        fontSize: 'clamp(16px, 4.2vw, 20px)',
                                        lineHeight: 1.2,
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <span
                                        ref={phone2MobileRef}
                                        style={{ display: 'block', width: '100%', whiteSpace: 'nowrap' }}
                                      >
                                        Text: (516)690-6900
                                      </span>
                                    </a>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Locations 卡片（仅移动端保留在 CTA 组内；桌面端将移到三卡并排行） */}
                          <div className="info-card info-card--wide mobile-only" style={{ marginTop: 35, width: '100%', maxWidth: 'none', alignSelf: 'stretch' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div className="info-head">
                                <i className="fa fa-map-marker info-icon" />
                                <h4 className="info-title">
                                  <span className="label-full">LOCATION</span>
                                  <span className="label-short">LOCATION</span>
                                </h4>
                              </div>
                              {/* Hide current location on mobile */}
                              <div className="desktop-only" ref={locAddr1Ref} style={{ padding: '0 24px' }}>
                                <a
                                  href="https://maps.app.goo.gl/pPB5F1MReEYgnhzXA"
                                  className="hover-underline-inset"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#777',
                                    textDecoration: 'none',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    width: '100%',
                                    fontSize: 'clamp(18px, 5vw, 21px)',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  Current Location (until 08/30): 303 Sunnyside Blvd Suite #10, Plainview, NY 11803
                                </a>
                              </div>
                              <div className="loc-spacer" style={{ height: 10 }} />
                              <div ref={locAddr2Ref} className="loc-text-block">
                                <a
                                  ref={locNewLinkRef}
                                  href="https://maps.app.goo.gl/qKWDmbQRu1Kkr7mE9"
                                  className="location-link hover-underline-inset mobile-only"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#111',
                                    textDecoration: 'none',
                                    display: 'block',
                                    width: '100%',
                                    fontSize: 'clamp(14px, 4vw, 19px)',
                                    lineHeight: 1.35,
                                  }}
                                >

                                  <span style={{ display: 'block', whiteSpace: 'nowrap' }}>6900 JERICHO TPKE, SUITE LL100,</span>
                                  <span style={{ display: 'block', whiteSpace: 'nowrap' }}>Syosset, NY 11791</span>
                                </a>
                                {/* Desktop-only 2-line version */}
                                <a
                                  href="https://maps.app.goo.gl/qKWDmbQRu1Kkr7mE9"
                                  className="location-link hover-underline-inset desktop-only"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#111',
                                    textDecoration: 'none',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    width: '100%',
                                    fontSize: 'clamp(18px, 2vw, 21px)',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  6900 JERICHO TPKE, SUITE LL100, Syosset, NY 11791
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 桌面端：Open House + Locations + Contact 三卡并排，等宽等高 */}
                        <div className="desktop-only">
                          <div className="info-row" style={{ gap: 16, alignItems: 'stretch' }}>
                            {/* Open House */}
                            <div
                              className="info-card card-scale-hover"
                              onMouseEnter={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.add('underline-active')); }}
                              onMouseLeave={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.remove('underline-active')); }}
                              onFocus={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.add('underline-active')); }}
                              onBlur={() => { document.querySelectorAll('.location-link').forEach(el => el.classList.remove('underline-active')); }}
                            >
                              <div style={{ textAlign: 'left' }}>
                                <h4
                                  role={homeCardDate.enabled ? "button" : undefined}
                                  tabIndex={homeCardDate.enabled ? 0 : -1}
                                  onClick={homeCardDate.enabled ? () => openHomeCardLink(homeCardDateLink) : undefined}
                                  onKeyDown={homeCardDate.enabled ? (e) => handleHomeCardKeyDown(e, homeCardDateLink) : undefined}
                                  style={{
                                    margin: '6px 0 12px',
                                    fontWeight: homeCardDate.fontWeight,
                                    letterSpacing: '.5px',
                                    color: homeCardDate.color,
                                    fontSize: homeCardDate.fontSizeDesktop,
                                    lineHeight: 1.1,
                                    cursor: homeCardDate.enabled ? 'pointer' : 'default',
                                  }}
                                >
                                  {homeCardDate.enabled && (
                                    <>
                                      <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{homeCardDate.line1}</span>
                                      <span style={{ display: 'block', whiteSpace: 'nowrap' }}>{homeCardDate.line2}</span>
                                    </>
                                  )}
                                </h4>
                                <div
                                  className="info-title info-title--open"
                                  role={homeCardText.enabled ? "button" : undefined}
                                  tabIndex={homeCardText.enabled ? 0 : -1}
                                  onClick={homeCardText.enabled ? () => openHomeCardLink(homeCardTextLink) : undefined}
                                  onKeyDown={homeCardText.enabled ? (e) => handleHomeCardKeyDown(e, homeCardTextLink) : undefined}
                                  style={{
                                    marginTop: 4,
                                    fontWeight: homeCardText.fontWeight,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    color: homeCardText.color,
                                    fontSize: homeCardText.fontSizeDesktop,
                                    cursor: homeCardText.enabled ? 'pointer' : 'default',
                                  }}
                                >
                                  {homeCardText.enabled && (
                                    <>
                                      <span ref={oneLine4Ref} className="line-1">{homeCardText.line1}</span>
                                      <span ref={oneLine5Ref} className="line-2">{homeCardText.line2}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Locations */}
                            <div className="info-card">
                              <div style={{ textAlign: 'center' }}>
                                <div className="info-head">
                                  <i className="fa fa-map-marker info-icon" />
                                  <h4 className="info-title">
                                    <span className="label-full">LOCATION</span>
                                    <span className="label-short">LOCATION</span>
                                  </h4>
                                </div>
                                {/* Desktop-only 3-line, auto-fit version */}
                                <div ref={locAddr2Ref} className="loc-text-block" style={{ padding: '0 24px' }}>
                                  <a
                                    ref={locDesktopLinkRef}
                                    href="https://maps.app.goo.gl/qKWDmbQRu1Kkr7mE9"
                                    className="location-link hover-underline-inset desktop-only"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: '#111',
                                      textDecoration: 'none',
                                      display: 'block',
                                      width: '100%',
                                      margin: '0 auto',
                                      textAlign: 'left',
                                      boxSizing: 'border-box',
                                      transform: 'none',
                                      fontSize: 'clamp(20px, 2.8vw, 30px)',
                                      lineHeight: 1.35,
                                    }}
                                  >
                                    <span style={{ display: 'block', whiteSpace: 'nowrap' }}>6900 JERICHO TPKE,</span>
                                    <span style={{ display: 'block', whiteSpace: 'nowrap' }}>SUITE LL100, Syosset,</span>
                                    <span style={{ display: 'block', whiteSpace: 'nowrap' }}>NY 11791</span>
                                  </a>
                                </div>
                              </div>
                            </div>

                            {/* Contact → Call or Text */}
                            <div className="info-card">
                              <div style={{ textAlign: 'center' }}>
                                <div className="info-head">
                                  <i className="fa fa-phone info-icon" />
                                  <h4 className="info-title">
                                    <span className="label-full">OFFICE OR TEXT</span>
                                    <span className="label-short">OFF/TXT</span>
                                  </h4>
                                </div>
                                <div className="contact-actions" style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', padding: '0 12px', boxSizing: 'border-box' }}>
                                  <a
                                    href="tel:+15167168888"
                                    className="pill-btn"
                                    style={{ display: 'flex', width: '100%', boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
                                  >
                                    <span className="pill-label" ref={phone1DesktopRef} style={{ width: '100%' }}>Office: (516)716-8888</span>
                                  </a>
                                  <a
                                    href="tel:+15166906900"
                                    className="pill-btn"
                                    style={{ display: 'flex', width: '100%', boxSizing: 'border-box', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
                                  >
                                    <span className="pill-label" ref={phone2DesktopRef} style={{ width: '100%' }}>Text: (516)690-6900</span>
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 下方横排容器已移除 Locations；Locations 已与 CTA 同容器内 */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* 以下与 HomeV2 保持一致（隐藏的 Why Us、Contact） */}
        <section className="section why-us" id="section2" data-section="section2" style={{ display: 'none' }}>
          <div className="container">
            <div className="row">
              <div className="col-md-12"><div className="section-heading"><h2 className="reveal">Why choose Long Island Academy?</h2></div></div>
              <div className="col-md-12">
                <div id="tabs" className="reveal delay-1">
                  <ul>
                    <li><a href="#tabs-1">AP Courses</a></li>
                    <li><a href="#tabs-2">SAT</a></li>
                    <li><a href="#tabs-3">Summer School</a></li>
                  </ul>
                  <section className="tabs-content">
                    <article id="tabs-1" className="reveal delay-2">
                      <div className="row">
                        <div className="col-md-6"><img src={chooseUsImage01} alt="" /></div>
                        <div className="col-md-6">
                          <h4>Advanced Placement Excellence</h4>
                          <p>Prepare for college-level academics with our expertly designed AP courses…</p>
                        </div>
                      </div>
                    </article>
                    <article id="tabs-2" className="reveal delay-2">
                      <div className="row">
                        <div className="col-md-6"><img src={chooseUsImage02} alt="" /></div>
                        <div className="col-md-6">
                          <h4>Master the SAT with Confidence</h4>
                          <p>Our SAT prep program offers structured sessions focusing on test-taking strategies…</p>
                        </div>
                      </div>
                    </article>
                    <article id="tabs-3" className="reveal delay-2">
                      <div className="row">
                        <div className="col-md-6"><img src={chooseUsImage03} alt="" /></div>
                        <div className="col-md-6">
                          <h4>Engaging Summer Learning Experience</h4>
                          <p>From July to August, our Summer School offers tailored classes for Grades 5–12…</p>
                        </div>
                      </div>
                    </article>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </section>

        <GlowEdge side="bottom" thickness={3} />

        <section className="section contact" data-section="section6" style={{ display: 'none' }}>
          <div className="container">
            <div className="row">
              <div className="col-md-12"><div className="section-heading" style={{ marginBottom: 8 }}><h2 className="reveal">Let’s Keep In Touch</h2></div></div>
              <div className="col-md-12">
                <div className="reveal delay-1" style={{ textAlign: "center", marginBottom: 20 }}>
                  <a
                    className="hover-underline-slide"
                    href="mailto:info@lailearning.com"
                    style={{
                      display: "inline-block",
                      padding: 0,
                      background: "transparent",
                      color: "#fff",
                      borderRadius: 0,
                      textDecoration: "none",
                      fontSize: 20,
                      fontWeight: 600,
                    }}
                  >
                    info@lialearning.com
                  </a>
                </div>
                <div id="map" className="reveal delay-1">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.932637976732!2d-73.52048212236367!3d40.80747403176274!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c283d5e13bd583%3A0xb3f4ca10fcf08bda!2s6900%20Jericho%20Tpke%2C%20Syosset%2C%20NY%2011791!5e0!3m2!1sen!2sus!4v1754892565734!5m2!1sen!2sus"
                    width="100%"
                    height="422"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="LIA Map"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

function sanitizeHomeCardLink(value) {
  const v = typeof value === "object" && value ? value : {};
  return {
    url: String(v.url ?? DEFAULT_CARD_LINK.url),
    openInNewTab: Boolean(v.openInNewTab ?? DEFAULT_CARD_LINK.openInNewTab),
  };
}

function openHomeCardLink(link) {
  const target = sanitizeHomeCardLink(link);
  const url = (target.url || "").trim();
  if (!url) return;
  if (target.openInNewTab) {
    window.open(url, "_blank", "noopener");
  } else {
    window.open(url, "_self");
  }
}

function handleHomeCardKeyDown(event, link) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    openHomeCardLink(link);
  }
}

export default Home;
